import { useMediaQuery, useTheme } from '@mui/material';

import { COLORS } from './constants';

interface PriceCredibilityStripProps {
  listPrice: number;
  advisorMinPrice: number;
  advisorMaxPrice: number;
}

const fmtPrice = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

// Compact form for tight layouts (e.g. $1,699,000 -> $1.699M) — keeps the
// headline price/estimate values on one line regardless of magnitude.
const fmtPriceCompact = (n: number) => {
  const abs = Math.round(n);
  const trim = (s: string) => s.replace(/0+$/, '').replace(/\.$/, '');
  if (abs >= 1_000_000) return `$${trim((abs / 1_000_000).toFixed(1))}M`;
  if (abs >= 1_000) return `$${trim((abs / 1_000).toFixed(1))}K`;
  return `$${abs}`;
};

export const PriceCredibilityStrip = ({
  listPrice, advisorMinPrice, advisorMaxPrice,
}: PriceCredibilityStripProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBelowRange = listPrice < advisorMinPrice;
  const isAboveRange = listPrice > advisorMaxPrice;
  const flagged = isBelowRange || isAboveRange;

  const trackMin = Math.min(listPrice, advisorMinPrice) * 0.97;
  const trackMax = Math.max(listPrice, advisorMaxPrice) * 1.03;
  const pct = (v: number) => ((v - trackMin) / (trackMax - trackMin)) * 100;

  // How much of the advisor's range sits below vs. above list price — this is
  // what actually shows the range "encasing" list price on both sides.
  const clippedListPrice = Math.min(Math.max(listPrice, advisorMinPrice), advisorMaxPrice);
  const belowListAmount = clippedListPrice - advisorMinPrice;
  const aboveListAmount = advisorMaxPrice - clippedListPrice;

  return (
    <div style={{
      background: COLORS.card, borderRadius: 25,
      backgroundImage: 'var(--tx-surface-light)', boxShadow: 'var(--tx-shadow)', padding: '20px',
    }}>
      <div style={{ fontSize: 15, fontWeight: 300, color: COLORS.text, marginBottom: 6 }}>
        Is the list price credible?
      </div>
      <div style={{ fontSize: 12, color: COLORS.textDim, lineHeight: 1.5, maxWidth: 480, marginBottom: 24 }}>
        Your advisor&apos;s estimate of true market value, compared against what the home is actually listed at.
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em', marginBottom: 4 }}>
            List price
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.text, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {isMobile ? fmtPriceCompact(listPrice) : fmtPrice(listPrice)}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: COLORS.textDim, letterSpacing: '0.06em', marginBottom: 4 }}>
            Advisor estimate
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, color: COLORS.text, lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {isMobile
              ? `${fmtPriceCompact(advisorMinPrice)}–${fmtPriceCompact(advisorMaxPrice)}`
              : `${fmtPrice(advisorMinPrice)}–${fmtPrice(advisorMaxPrice)}`}
          </div>
        </div>
      </div>

      {/* Range track */}
      <div style={{ position: 'relative', height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.08)', margin: '0 2px' }}>
        <div style={{
          position: 'absolute', top: 0, bottom: 0, borderRadius: 4,
          left: `${pct(advisorMinPrice)}%`,
          width: `${pct(advisorMaxPrice) - pct(advisorMinPrice)}%`,
          background: `linear-gradient(90deg, ${COLORS.accent1}, ${COLORS.urgent})`,
        }} />
        <div
          title={`List Price: ${fmtPrice(listPrice)}`}
          style={{
            position: 'absolute', top: -6, width: 3, height: 20, borderRadius: 2,
            left: `${pct(listPrice)}%`, transform: 'translateX(-1.5px)',
            background: flagged ? COLORS.urgent : COLORS.text,
          }}
        />
      </div>
      <div style={{ position: 'relative', height: 16, marginTop: 8 }}>
        {belowListAmount > 0 && (
          <span style={{
            position: 'absolute', left: `${pct(advisorMinPrice)}%`,
            fontSize: 10, fontWeight: 600, color: COLORS.accent2, whiteSpace: 'nowrap',
          }}>
            {fmtPrice(belowListAmount)} below list
          </span>
        )}
        {aboveListAmount > 0 && (
          <span style={{
            position: 'absolute', left: `${pct(advisorMaxPrice)}%`, transform: 'translateX(-100%)',
            fontSize: 10, fontWeight: 600, color: COLORS.accent2, whiteSpace: 'nowrap',
          }}>
            {fmtPrice(aboveListAmount)} above list
          </span>
        )}
      </div>

      {isBelowRange && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          background: 'rgba(255,154,77,0.1)', border: 0,
          borderRadius: 10, padding: '10px 12px', marginTop: 20,
        }}>
          <span style={{ fontSize: 12, color: COLORS.urgent, lineHeight: 1.5 }}>
            Listed {fmtPrice(advisorMinPrice - listPrice)} below your advisor&apos;s estimate — likely
            power-priced to drive up demand and competing offers.
          </span>
        </div>
      )}
      {isAboveRange && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          background: 'rgba(255,154,77,0.1)', border: 0,
          borderRadius: 10, padding: '10px 12px', marginTop: 20,
        }}>
          <span style={{ fontSize: 12, color: COLORS.urgent, lineHeight: 1.5 }}>
            Listed {fmtPrice(listPrice - advisorMaxPrice)} above your advisor&apos;s estimate — there may be
            room to negotiate toward true value before even factoring in inspection findings.
          </span>
        </div>
      )}
    </div>
  );
};

export default PriceCredibilityStrip;
